import React, { useEffect, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'

import { Dialog, snackbar } from 'components'
import { useApi, useApiCall } from 'hooks'
import { ERRORS } from 'consts'

import {
  CoolingDownConfigResponse,
  CoolingDownThreshold,
  UpdateCoolingDownConfigPayload,
} from '../../types'
import { getCoolingDownConfig, updateCoolingDownConfig } from '../../api'

const isPositiveInteger = (value: string): boolean => {
  const parsed = Number(value)
  return value.trim() !== '' && Number.isInteger(parsed) && parsed > 0
}

interface CoolingDownConfigDialogProps {
  handleClose: () => void
  onDone: () => void
}

const CoolingDownConfigDialog: React.FC<CoolingDownConfigDialogProps> = ({
  handleClose,
  onDone,
}) => {
  const [{ data: config, isLoading: isLoadingConfig }] = useApi<
    CoolingDownConfigResponse,
    CoolingDownConfigResponse
  >(getCoolingDownConfig, response => response, {
    baseData: {
      tag: { id: 0, title: '', description: '', active: false, code: null },
      thresholds: [],
    },
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [daysByRitmo, setDaysByRitmo] = useState<Record<string, string>>({})
  const [updateConfigApi, isSaving] = useApiCall<
    UpdateCoolingDownConfigPayload,
    CoolingDownConfigResponse
  >(updateCoolingDownConfig)

  useEffect(() => {
    if (!config?.thresholds?.length) return
    const next: Record<string, string> = {}
    config.thresholds.forEach(threshold => {
      next[threshold.ritmoCode] = String(threshold.days)
    })
    setDaysByRitmo(next)
  }, [config])

  const thresholds: CoolingDownThreshold[] = config?.thresholds ?? []
  const hasInvalidDays = thresholds.some(
    threshold => !isPositiveInteger(daysByRitmo[threshold.ritmoCode] ?? ''),
  )
  const isSubmitDisabled = isLoadingConfig || isSaving || !thresholds.length || hasInvalidDays

  const handleDaysChange = (ritmoCode: string, value: string) => {
    setDaysByRitmo(prev => ({ ...prev, [ritmoCode]: value }))
  }

  const handleSubmit = async () => {
    if (hasInvalidDays) {
      snackbar.show('Los valores ingresados deben ser números enteros mayores a 0')
      return
    }

    try {
      await updateConfigApi({
        thresholds: thresholds.map(threshold => ({
          ritmoCode: threshold.ritmoCode,
          days: Number(daysByRitmo[threshold.ritmoCode]),
        })),
      })
      snackbar.show('Configuración de Enfriándose actualizada')
      onDone()
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/member-delimiter-style
      const error = err as { status?: number; message?: string }
      if (error?.status === 400) {
        snackbar.show(error?.message || 'Error al guardar la configuración')
      } else {
        snackbar.show('No se pudo guardar la configuración. Intente de nuevo.')
      }
    }
  }

  return (
    <Dialog
      title="Configurar Enfriándose"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoadingConfig || isSaving}
      okButtonText="Guardar"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={handleSubmit}
      contentStyle={{ minWidth: 480 }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Días sin visita (estrictamente mayores al umbral) para usuarios Activos con ritmo
            Semanal, Quincenal o Mensual y más de 90 días desde el alta. No aplica a &quot;Menos de
            una por mes&quot;.
          </Typography>
        </Grid>
        {thresholds.map(threshold => {
          const value = daysByRitmo[threshold.ritmoCode] ?? ''
          const hasError = value !== '' && !isPositiveInteger(value)
          return (
            <Grid item xs={12} key={threshold.ritmoCode}>
              <TextField
                fullWidth
                type="number"
                label={threshold.title}
                value={value}
                onChange={e => handleDaysChange(threshold.ritmoCode, e.target.value)}
                disabled={isLoadingConfig || isSaving}
                required
                error={hasError}
                helperText={
                  hasError
                    ? 'Debe ser un número entero mayor a 0'
                    : 'Días sin visitas para asignar Enfriándose (ej. 14 → entra desde el día 15)'
                }
                InputProps={{
                  inputProps: { min: 1, step: 1 },
                }}
              />
            </Grid>
          )
        })}
        {!isLoadingConfig && thresholds.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2">No hay ritmos elegibles para configurar.</Typography>
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

export { CoolingDownConfigDialog }
